import { Modal } from "@App/Components/Modal";
import { Fragment, type FunctionComponent } from "react";
import { AddressForm } from "@App/Components/molecules/checkout/AddressForm";
import { Button } from "@App/Components/Funnel/Button";
import { useTranslation } from "react-i18next";
import { SuccessAnimation } from "@App/Components/atoms/animations/SuccessAnimation";
import { Alert } from "@App/Components/common/Alert";

type PickUpAndReturnAddressModalProps = {
  open: boolean;
  loading: boolean;
  error?: string;
  values: {
    street: {
      value: string;
      validationMessage?: string;
      onChange: (street: string) => void;
    };
    city: {
      value: string;
      validationMessage?: string;
      onChange: (city: string) => void;
    };
    postcode: {
      value: string;
      validationMessage?: string;
      onChange: (postcode: string) => void;
    };
    addition: {
      value: string;
      validationMessage?: string;
      onChange: (addition: string) => void;
    };
  };
  onSubmit: () => void;
  onCancel: () => void;
};

export const PickUpAndReturnAddressModal: FunctionComponent<PickUpAndReturnAddressModalProps> = ({
  open,
  loading,
  error,
  values,
  onSubmit,
  onCancel,
}: PickUpAndReturnAddressModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      closeable={false}
      showButton={false}
      width={"max-w-3xl"}
      title={t("molecules.common.modals.pickUpAndReturnAddressModal.title")}
    >
      <Fragment>
        {loading && <SuccessAnimation />}
        {!loading && (
          <div>
            <p>{t("molecules.common.modals.pickUpAndReturnAddressModal.description")}</p>
            <AddressForm
              onSubmit={onSubmit}
              street={{
                value: values.street.value,
                onChange: values.street.onChange,
                validationMessage: values.street.validationMessage,
                required: true,
              }}
              city={{
                value: values.city.value,
                onChange: values.city.onChange,
                validationMessage: values.city.validationMessage,
                required: true,
              }}
              postcode={{
                value: values.postcode.value,
                onChange: values.postcode.onChange,
                validationMessage: values.postcode.validationMessage,
                required: true,
              }}
              addition={{
                value: values.addition.value,
                onChange: values.addition.onChange,
                validationMessage: values.addition.validationMessage,
                required: false,
              }}
            />
            {error && <Alert display={true} title={error} />}
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
              <Button
                width="md:w-fit"
                title={t("molecules.common.modals.pickUpAndReturnAddressModal.submit")}
                onClick={onSubmit}
              />
              <Button
                width="md:w-fit"
                title={t("molecules.common.modals.pickUpAndReturnAddressModal.cancel")}
                outline
                onClick={onCancel}
              />
            </div>
          </div>
        )}
      </Fragment>
    </Modal>
  );
};
